import { Operation, OperationChain, OperationChainLink } from '@/types';
import { selectScenario } from '../scenarios/scenarioSlice';
import { addPaths, removePaths } from '../paths/pathsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import { Path } from '@/paths/pathsSlice';

type LinkedOperation = Operation & { chain_link: OperationChainLink };

export const useOptimizationPaths = () => {
  const dispatch = useDispatch();
  const scenario = useSelector(selectScenario);

  const toLinkedOperations = useCallback(
    (chain: OperationChain) => {
      return chain.chain_links.map((chain_link) => {
        const operation = scenario.operations.find(
          (op) => op.id === chain_link.operation_id,
        );
        return {
          ...operation,
          chain_link,
        } as LinkedOperation;
      });
    },
    [scenario.operations],
  );

  const toPathNodeIds = useCallback((operations: LinkedOperation[]) => {
    return operations.reduce((prev: number[], operation) => {
      if (operation?.path_node_ids?.length > 0) {
        return prev.concat(operation.path_node_ids.map(Number));
      }
      const path_node_ids: number[] = [];
      if (
        operation.receipt_point.node &&
        ['KEXCHANGE', 'METER_BOUNCE'].includes(operation.receipt_asset_type)
      ) {
        path_node_ids.push(operation.receipt_point.node);
      }
      if (
        operation.delivery_point.node &&
        ['KEXCHANGE', 'METER_BOUNCE'].includes(operation.delivery_asset_type)
      ) {
        path_node_ids.push(operation.delivery_point.node);
      }
      return prev.concat(path_node_ids);
    }, []);
  }, []);

  const paths = useMemo<Path[]>(() => {
    return scenario.operation_chains.map((chain) => {
      const operations = toLinkedOperations(chain);
      const path_node_ids = toPathNodeIds(operations);
      return {
        id: chain.id,
        path_node_ids,
        receipt_description: operations[0]?.receipt_description,
        delivery_description:
          operations[operations.length - 1]?.delivery_description,
        operation_chain: chain,
      };
    });
  }, [scenario.operation_chains, toLinkedOperations, toPathNodeIds]);

  useEffect(() => {
    dispatch(addPaths(paths));
    return () => {
      dispatch(removePaths(paths.map((path) => path.id)));
    };
  }, [paths]);

  return paths;
};
